import { FormEvent, useEffect, useMemo, useState } from "react"
import { t } from "../../i18n"
import { ManagementCompany, SagePayrollExportPreview } from "../../Interfaces"
import { SageApiError } from "../../utils/sageApiError"
import { defaultPayrollRange, isValidPayrollRange } from "../../utils/payrollExport"
import { defaultPayrollCompanyId } from "../../utils/payrollCompanies"
import { formatCompanyContext } from "../../utils/companyAccess"
import { sagePayrollExportService } from "../../services/sagePayrollExportService"
import { cwInputClassName } from "./CwFormModal"

export default function PayrollExportPanel({
  companyId,
  companyCode,
  companies,
  allowCompanySelect = false,
  companiesError,
  initialPreview = null,
}: {
  companyId?: number
  companyCode?: string
  companies?: ManagementCompany[]
  allowCompanySelect?: boolean
  companiesError?: string
  initialPreview?: SagePayrollExportPreview | null
}) {
  const defaults = useMemo(() => defaultPayrollRange(), [])
  const companyChoices = useMemo(() => companies ?? [], [companies])
  const companyKey = companyChoices.map((company) => company.id).join(",")
  const showCompanyField = Boolean(companies)
  const canPickCompany = allowCompanySelect && companyChoices.length > 1
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(
    () => defaultPayrollCompanyId(companyChoices, companyId),
  )
  const [from, setFrom] = useState(defaults.from)
  const [to, setTo] = useState(defaults.to)
  const [dateError, setDateError] = useState("")
  const [companyError, setCompanyError] = useState("")
  const [error, setError] = useState("")
  const [previewing, setPreviewing] = useState(false)
  const [downloading, setDownloading] = useState<"csv" | "xlsx" | "">("")
  const [preview, setPreview] = useState<SagePayrollExportPreview | null>(initialPreview)

  useEffect(() => {
    const choices = companies ?? []
    setSelectedCompanyId((current) => {
      if (current && choices.some((company) => company.id === current)) {
        return current
      }
      const nextId = defaultPayrollCompanyId(choices, companyId)
      if (nextId !== current) setPreview(null)
      return nextId
    })
  }, [companyKey, companyId, companies])

  const activeCompany = useMemo(() => {
    if (companyChoices.length) {
      return companyChoices.find((company) => company.id === selectedCompanyId) || companyChoices[0]
    }
    if (companyId) {
      return { id: companyId, name: "", code: companyCode || "" }
    }
    return null
  }, [companyChoices, selectedCompanyId, companyId, companyCode])

  const validateCompany = () => {
    if (!activeCompany?.id) {
      setCompanyError(t("payroll.companyRequired"))
      return false
    }
    setCompanyError("")
    return true
  }

  const validateRange = () => {
    if (!from || !to) {
      setDateError(t("payroll.dateRequired"))
      return false
    }
    if (!isValidPayrollRange(from, to)) {
      setDateError(t("payroll.dateOrder"))
      return false
    }
    setDateError("")
    return true
  }

  const handlePreview = async (event: FormEvent) => {
    event.preventDefault()
    if (!validateCompany() || !validateRange()) return
    setError("")
    setPreviewing(true)
    try {
      const result = await sagePayrollExportService.preview(activeCompany!.id, { from, to })
      setPreview(result)
    } catch (previewError) {
      setPreview(null)
      setError(previewError instanceof SageApiError && previewError.message
        ? previewError.message
        : t("payroll.loadFailed"))
    } finally {
      setPreviewing(false)
    }
  }

  const handleDownload = async (format: "csv" | "xlsx") => {
    if (!validateCompany() || !validateRange()) return
    setError("")
    setDownloading(format)
    try {
      await sagePayrollExportService.download(activeCompany!.id, {
        from,
        to,
        format,
        companyCode: activeCompany?.code || companyCode,
      })
    } catch (downloadError) {
      setError(downloadError instanceof SageApiError && downloadError.message
        ? downloadError.message
        : t("payroll.downloadFailed"))
    } finally {
      setDownloading("")
    }
  }

  const handleCompanyChange = (nextId: number) => {
    setSelectedCompanyId(nextId)
    setPreview(null)
    setCompanyError("")
    setError("")
  }

  return (
    <section className="mb-6 bg-white rounded-2xl shadow-lg p-5 md:p-6 font-source">
      <h2 className="font-oswald text-2xl font-bold text-primary-dark">{t("payroll.title")}</h2>
      <p className="mt-1 text-gray-600">{t("payroll.helper")}</p>

      <form className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end" onSubmit={handlePreview}>
        {showCompanyField ? (
          <div className="flex-1">
            <label htmlFor="payroll-company" className="block font-semibold text-primary-dark mb-1">
              {t("payroll.companyLabel")}
            </label>
            {canPickCompany ? (
              <select
                id="payroll-company"
                value={activeCompany?.id ?? ""}
                onChange={(event) => handleCompanyChange(Number(event.target.value))}
                className={cwInputClassName}
              >
                {companyChoices.map((company) => (
                  <option key={company.id} value={company.id}>
                    {formatCompanyContext(company)}
                  </option>
                ))}
              </select>
            ) : (
              <p
                id="payroll-company"
                className={`${cwInputClassName} ${activeCompany ? "" : "text-gray-500"}`}
              >
                {activeCompany
                  ? formatCompanyContext(activeCompany)
                  : t("payroll.companyRequired")}
              </p>
            )}
          </div>
        ) : null}
        <div className="flex-1">
          <label htmlFor="payroll-from" className="block font-semibold text-primary-dark mb-1">
            {t("payroll.fromLabel")}
          </label>
          <input
            id="payroll-from"
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value)
              if (dateError) setDateError("")
            }}
            className={cwInputClassName}
          />
        </div>
        <div className="flex-1">
          <label htmlFor="payroll-to" className="block font-semibold text-primary-dark mb-1">
            {t("payroll.toLabel")}
          </label>
          <input
            id="payroll-to"
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value)
              if (dateError) setDateError("")
            }}
            className={cwInputClassName}
          />
        </div>
        <button
          type="submit"
          disabled={previewing || Boolean(downloading)}
          className="rounded-full bg-primary px-5 py-3 font-semibold text-white disabled:opacity-60"
        >
          {previewing ? t("payroll.previewing") : t("payroll.preview")}
        </button>
      </form>
      {companyError || companiesError ? (
        <p className="mt-2 text-sm text-red-600" role="alert">{companyError || companiesError}</p>
      ) : null}
      {dateError ? (
        <p className="mt-2 text-sm text-red-600" role="alert">{dateError}</p>
      ) : null}
      {error ? (
        <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>
      ) : null}

      {preview ? (
        <div className="mt-5">
          <p className="font-semibold text-primary-dark">
            {preview.rowCount > 0
              ? t("payroll.rowCount", { count: preview.rowCount })
              : t("payroll.emptyRows")}
          </p>
          {preview.rows.length > 0 ? (
            <div className="mt-3 overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm text-start">
                <thead>
                  <tr className="bg-secondary-extra-light text-primary-dark">
                    <th className="px-3 py-2 font-semibold">{t("payroll.columnPayDate")}</th>
                    <th className="px-3 py-2 font-semibold">{t("payroll.columnEmployee")}</th>
                    <th className="px-3 py-2 font-semibold">{t("payroll.columnJob")}</th>
                    <th className="px-3 py-2 font-semibold">{t("payroll.columnCostCode")}</th>
                    <th className="px-3 py-2 font-semibold text-end">{t("payroll.columnHours")}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 25).map((row, index) => (
                    <tr key={`${row.empnum}-${row.paydte}-${row.jobnum}-${row.cstcde}-${index}`} className="border-t border-gray-100">
                      <td className="px-3 py-2">{String(row.paydte ?? "")}</td>
                      <td className="px-3 py-2">{String(row.empnum ?? "")}</td>
                      <td className="px-3 py-2">{String(row.jobnum ?? "")}</td>
                      <td className="px-3 py-2">{String(row.cstcde ?? "")}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{String(row.payhrs ?? "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {preview.warnings.length > 0 ? (
            <div className="mt-4 rounded-xl bg-secondary-extra-light px-4 py-3">
              <p className="font-semibold text-primary-dark">{t("payroll.warningsTitle")}</p>
              <p className="text-sm text-gray-600">{t("payroll.warningsHelper")}</p>
              <ul className="mt-2 list-disc pl-5 text-sm text-primary-dark space-y-1">
                {preview.warnings.map((warning, index) => (
                  <li key={`${warning.reason}-${warning.workDayId ?? index}`}>
                    {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleDownload("csv")}
              disabled={Boolean(downloading) || previewing}
              className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-60"
            >
              {downloading === "csv" ? t("payroll.downloading") : t("payroll.downloadCsv")}
            </button>
            <button
              type="button"
              onClick={() => void handleDownload("xlsx")}
              disabled={Boolean(downloading) || previewing}
              className="rounded-full border-2 border-primary bg-white px-5 py-2 font-semibold text-primary disabled:opacity-60"
            >
              {downloading === "xlsx" ? t("payroll.downloading") : t("payroll.downloadXlsx")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
